import { useState, useCallback } from 'react';
import { ActivityLogEntry, ActivityLogResponse, ActivityLogPagination } from '../types/business';

interface UseActivityLogOptions {
  initialLimit?: number;
  customEndpointPath?: boolean; // Change to boolean flag
}

interface UseActivityLogReturn {
  activities: ActivityLogEntry[];
  isLoading: boolean;
  error: string | null;
  pagination: ActivityLogPagination | null;
  fetchActivities: (businessId: string, limit?: number, offset?: number) => Promise<ActivityLogResponse>;
  refreshActivities: (businessId: string) => Promise<void>;
  loadMoreActivities: (businessId: string) => Promise<void>;
  resetActivities: () => void;
}

export const useActivityLog = (options: UseActivityLogOptions = {}): UseActivityLogReturn => {
  const { initialLimit = 50, customEndpointPath = false } = options;
  
  const [activities, setActivities] = useState<ActivityLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<ActivityLogPagination | null>(null);

  const fetchActivities = useCallback(async (
    businessIdOrPath: string, 
    limit: number = initialLimit, 
    offset: number = 0
  ): Promise<ActivityLogResponse> => {
    try {
      // Use custom endpoint path if flag is true, otherwise use default business activity endpoint
      const endpoint = customEndpointPath 
        ? `/api/business/${businessIdOrPath}` 
        : `/api/business/${businessIdOrPath}/activity`;
        
      const response = await fetch(
        `${endpoint}?limit=${limit}&offset=${offset}`,
        {
          method: 'GET',
          credentials: 'same-origin'
        }
      );
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        return {
          success: true,
          activities: data.activities || [],
          pagination: data.pagination || {
            total: 0,
            limit,
            offset,
            hasMore: false
          }
        };
      } else {
        return {
          success: false,
          activities: [],
          pagination: {
            total: 0,
            limit,
            offset,
            hasMore: false
          },
          error: data.error || 'Failed to fetch activities'
        };
      }
    } catch (error) {
      console.error('Error fetching activities:', error);
      return {
        success: false,
        activities: [],
        pagination: {
          total: 0,
          limit,
          offset,
          hasMore: false
        },
        error: error instanceof Error ? error.message : 'An unexpected error occurred'
      };
    }
  }, [initialLimit, customEndpointPath]);

  const refreshActivities = useCallback(async (businessId: string) => {
    if (!businessId) {
      console.log('No business ID available for fetching activities');
      return;
    }

    setIsLoading(true);
    setError(null);
    
    const result = await fetchActivities(businessId, initialLimit, 0);
    
    if (result.success) {
      setActivities(result.activities);
      setPagination(result.pagination);
    } else {
      setError(result.error || 'Failed to fetch activities');
      setActivities([]);
      setPagination(null);
    }
    
    setIsLoading(false);
  }, [fetchActivities, initialLimit]);

  const loadMoreActivities = useCallback(async (businessId: string) => {
    if (!businessId || !pagination || !pagination.hasMore || isLoading) {
      return;
    }

    setIsLoading(true);
    setError(null);
    
    const nextOffset = pagination.offset + pagination.limit;
    const result = await fetchActivities(businessId, pagination.limit, nextOffset);
    
    if (result.success) {
      setActivities(prev => [...prev, ...result.activities]);
      setPagination(result.pagination);
    } else {
      setError(result.error || 'Failed to load more activities');
    }
    
    setIsLoading(false);
  }, [fetchActivities, pagination, isLoading]);

  const resetActivities = useCallback(() => {
    setActivities([]);
    setPagination(null);
    setError(null);
    setIsLoading(false);
  }, []);

  return {
    activities,
    isLoading,
    error,
    pagination,
    fetchActivities,
    refreshActivities,
    loadMoreActivities,
    resetActivities
  };
};